import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { AutosaveTextInput } from '@/components/Admin/Autosave/AutosaveTextInput';
import { ClosureRangePicker } from '@/components/Admin/ClosureRangePicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  closurePeriodDatesLabel,
  closureReasonMaxLength,
  validateClosurePeriod,
  type ClosurePeriodType,
} from '@/types/types_closure';

/**
 * One absence with the key its owner addresses it by — an array index's identity
 * on an experience, a map key on a supplier. The editor never invents one, so
 * neither store has to be reshaped to use this.
 */
export interface ClosurePeriodRowType {
  key: string;
  period: ClosurePeriodType;
}

/** Absences this record inherits and cannot edit here, and where they come from. */
export interface InheritedClosuresType {
  periods: ClosurePeriodType[];
  /** Whose they are, e.g. the supplier's name. */
  sourceName: string;
  /** Where they are edited, if the reader can go there. */
  sourceHref?: string;
}

interface ClosurePeriodsFieldProps {
  rows: ClosurePeriodRowType[];
  /** Today, `yyyy-mm-dd`. Only used to mark absences that are already over. */
  today: string;
  /** The line under the heading, since the same list means slightly different things per record. */
  hint: string;
  /** What to say when there are none. */
  emptyMessage: string;
  onAdd: (period: ClosurePeriodType) => void;
  onChangeReason: (key: string, reason: string) => void;
  onRemove: (key: string) => void;
  inherited?: InheritedClosuresType;
}

/**
 * The "Artisan away" list: spans of dates a thing cannot be run on, added,
 * annotated and removed.
 *
 * Shared by the experience form and the supplier form, which hold the same list
 * for different reasons — an experience's own absences, and the artisan's, typed
 * once for every experience they run. The editing is identical, so it lives here
 * once; what differs is how each store addresses a row, which is why rows arrive
 * already keyed and every callback names a key rather than an index.
 *
 * Editing an absence's dates in place is not offered, the same call as the slot
 * list on the experience form: the dates *are* the record, and "which days" is
 * exactly the thing that must not be quietly rewritten under a concierge who has
 * already read it. The reason is not — that is a note, and correcting a typo in
 * one should not mean retyping the span.
 */
export function ClosurePeriodsField({
  rows,
  today,
  hint,
  emptyMessage,
  onAdd,
  onChangeReason,
  onRemove,
  inherited,
}: ClosurePeriodsFieldProps) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  function addPeriod() {
    // Both ends always come from the picker together — a single day off is the
    // same date twice — so there is no half-typed span to fill in here.
    const draft: ClosurePeriodType = { startDate, endDate, reason: reason.trim() };
    // Checked against this record's own rows only. An absence that repeats one
    // inherited from the supplier is redundant rather than wrong — and refusing
    // it would mean an admin could not record why *this* experience is off that
    // week.
    const problem = validateClosurePeriod(draft, rows.map((row) => row.period));
    if (problem) {
      setFormError(problem);
      return;
    }
    setFormError(null);
    onAdd(draft);
    setStartDate('');
    setEndDate('');
    setReason('');
  }

  return (
    <div className='tw:space-y-3'>
      <div>
        <p className='tw:text-xs tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase'>
          Artisan away
        </p>
        <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>{hint}</p>
      </div>

      <div className='tw:flex tw:flex-wrap tw:items-end tw:gap-3'>
        <div>
          <span className='tw:mb-1 tw:block tw:text-xs tw:tracking-wider tw:text-muted-foreground tw:uppercase'>
            Dates
          </span>
          <ClosureRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={(first, last) => {
              setStartDate(first);
              setEndDate(last);
            }}
          />
        </div>
        <label className='tw:block tw:min-w-56 tw:flex-1'>
          <span className='tw:mb-1 tw:block tw:text-xs tw:tracking-wider tw:text-muted-foreground tw:uppercase'>
            Reason (optional)
          </span>
          <Input
            value={reason}
            maxLength={closureReasonMaxLength}
            placeholder='e.g. family holiday'
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <Button type='button' variant='outline' onClick={addPeriod} disabled={!startDate}>
          <Plus className='tw:size-4' />
          Add closure
        </Button>
      </div>

      {formError && <p className='tw:text-sm tw:text-destructive'>{formError}</p>}

      {rows.length === 0 ? (
        <p className='tw:text-sm tw:text-muted-foreground'>{emptyMessage}</p>
      ) : (
        <ul className='tw:divide-y tw:rounded-md tw:border'>
          {rows.map(({ key, period }) => (
            <li
              key={key}
              className='tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:px-3 tw:py-2 tw:text-sm'
            >
              <span className='tw:w-72 tw:shrink-0'>{closurePeriodDatesLabel(period)}</span>
              {period.endDate < today && (
                <span className='tw:rounded-full tw:bg-muted tw:px-2 tw:py-0.5 tw:text-xs tw:text-muted-foreground'>
                  Past
                </span>
              )}
              <div className='tw:min-w-48 tw:flex-1'>
                <AutosaveTextInput
                  currentValue={period.reason}
                  placeholder='Reason (optional)'
                  onCommitChange={(v) => onChangeReason(key, v.slice(0, closureReasonMaxLength))}
                />
              </div>
              <button
                type='button'
                onClick={() => onRemove(key)}
                aria-label={`Remove the closure on ${closurePeriodDatesLabel(period)}`}
                className='tw:text-muted-foreground tw:transition-colors tw:hover:text-destructive'
              >
                <Trash2 className='tw:size-4' />
              </button>
            </li>
          ))}
        </ul>
      )}

      {inherited && inherited.periods.length > 0 && (
        <InheritedClosureList inherited={inherited} today={today} />
      )}
    </div>
  );
}

/**
 * The absences this record gets from somewhere else, listed but not editable.
 *
 * Shown rather than hidden because the question an admin is on this screen to
 * answer is "which days is this off", and an answer that silently omits the six
 * weeks the artisan is away is the wrong answer. Read-only because editing them
 * here would mean editing them for every other experience the supplier runs,
 * which is a decision to make on the supplier's own page — so that is where the
 * link goes.
 */
function InheritedClosureList({ inherited, today }: { inherited: InheritedClosuresType; today: string }) {
  // Past absences are dropped, as they are in the summary a concierge reads:
  // last summer's holiday explains nothing about a date anybody can still book,
  // and this list is not where they are deleted from.
  const upcoming = inherited.periods.filter((period) => period.endDate >= today);
  if (upcoming.length === 0) return null;

  return (
    <div className='tw:rounded-md tw:border tw:border-dashed tw:bg-muted/30 tw:px-3 tw:py-2.5'>
      <p className='tw:text-xs tw:text-muted-foreground'>
        Also closed with {inherited.sourceName}
        {inherited.sourceHref && (
          <>
            {' — '}
            <Link to={inherited.sourceHref} className='tw:underline tw:hover:text-foreground'>
              edit these on the supplier
            </Link>
          </>
        )}
      </p>
      <ul className='tw:mt-1.5 tw:space-y-0.5'>
        {upcoming.map((period) => (
          <li key={`${period.startDate}_${period.endDate}`} className='tw:text-sm tw:text-muted-foreground'>
            {closurePeriodDatesLabel(period)}
            {period.reason && ` — ${period.reason}`}
          </li>
        ))}
      </ul>
    </div>
  );
}
